import { Component, OnInit } from '@angular/core';
import { Subscription, forkJoin } from 'rxjs';
import { TrendService } from '../services/trend.service';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder } from '@angular/forms';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { LoadingService } from '../services/loading.service';
import { MatFormField, MatSelect, MatOption } from '@angular/material/select';
import { NgFor, NgIf } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { DateSelectionComponent } from '../date-selection/date-selection.component';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([BarChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent, CanvasRenderer]);

dayjs.extend(utc);
dayjs.extend(timezone);

@Component({
    selector: 'app-trend-sample-rate',
    templateUrl: './trend-sample-rate.component.html',
    styleUrls: ['./trend-sample-rate.component.scss'],
    imports: [MatFormField, MatSelect, NgFor, MatOption, MatButton, DateSelectionComponent, NgIf, NgxEchartsDirective],
    providers: [provideEchartsCore({ echarts })]
})
export class TrendSampleRateComponent implements OnInit {
  subscription:Subscription;
  chartOption: any;  

  lines: [{ Line: string }];
  plant: string;
  fromDateString: string;
  toDateString: string;
  fg_hdr: FormGroup = new FormGroup('');
  manualInput = false;
  selectedLine: string[] = [];
  showChart = false;
  currentShift = 'True';

  private readonly palette = [
    '#5b6ee1',
    '#22c7d6',
    '#ff6b6b',
    '#ffb020',
    '#67c587',
    '#b39ddb',
    '#6ec6ff',
    '#7986cb',
    '#26c6da',
    '#ef5350',
    '#fbc02d',
    '#81c784'
  ];

  constructor(
    private service:TrendService,
    private route: ActivatedRoute,
    private loadingService: LoadingService,
    private formBuilder: FormBuilder) { }
    
  ngOnInit() {
    this.plant = this.route.snapshot.paramMap.get('plant');
    this.chartOption = {};

    this.service.getLines(this.plant).subscribe( (out) => {
      console.log("Lines", out);
        this.lines = out.Body;

        if (this.lines?.length >= 1) {
        this.selectedLine = [this.lines[0].Line];
    }

    this.fetch();
    });
  }

fetch() {
  this.loadingService.show();
  if (this.subscription){ this.subscription.unsubscribe();}

  if (this.selectedLine.length > 0) {
    const requests = this.selectedLine.map(line =>
      this.service.getSampleRate(this.plant, this.fromDateString, this.toDateString, line)
    );

  this.subscription = forkJoin(requests).subscribe({
    next: (results) => {
        let shiftData = [];
        const operatorData = [];

        results.forEach(result => {
            if (!shiftData.length && result?.Body?.shiftData) {
                shiftData = result.Body.shiftData;
            }

            if (result?.Body?.operatorData) {
                operatorData.push(...result.Body.operatorData);
            }
        });

        if (shiftData.length && operatorData.length) {
          this.buildChartOption(shiftData, operatorData);
          this.showChart = true;
        } else {
          this.showChart = false;
        }
      },
      error: () => {
        this.showChart = false;
      },
      complete: () => {
        this.loadingService.hide();
      }
    });
  } else {
    this.showChart = false;
    this.loadingService.hide();
  }
}

  changeLine() {
      this.fetch();
  }

  onDateChange(e: any) {
    this.fromDateString = e.fromDate;
    this.toDateString = e.toDate;
    this.currentShift = e.currentShift;
    this.fetch();
  }

  private buildChartOption(shiftData: any[], operatorData: any[]) {
    const categories = shiftData.map(item => item.label ?? item.Label ?? '');
    const shouldRotateLabels = categories.length >= 6;

    const series = operatorData.map((item, index) => {
      const color = this.palette[index % this.palette.length];

      return {
        name: item.seriesname ?? item.seriesName ?? `Series ${index + 1}`,
        type: 'bar',
        stack: 'total',
        barMaxWidth: 40,
        itemStyle: {
          color
        },
        label: {
          show: true,
          position: 'inside',
          color: '#000',
          fontSize: 10,
          formatter: (params: any) => {
            if (params.value === null || params.value === undefined || params.value === 0) {
              return '0%';
            }
            return `${Number(params.value).toFixed(2)}%`;
          }
        },
        emphasis: {
          focus: 'series'
        },
        data: (item.data || []).map((d: any) => {
          const value = d?.value;
          return value === null || value === undefined || value === '' ? 0 : Number(value);
        })
      };
    });

    this.chartOption = {
      title: {
        text: 'EOLA Sample Rate',
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 14,
          fontWeight: '600',
          color: '#666'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: '#fff',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#444'
        },
        formatter: (params: any[]) => this.getTooltipFormatter(params)
      },
      legend: {
        type: 'scroll',
        orient: 'vertical',
        right: 10,
        top: 'middle',
        itemWidth: 14,
        itemHeight: 14,
        textStyle: {
          color: '#666',
          fontSize: 12
        },
        data: series.map(s => s.name)
      },
      grid: {
        left: 65,
        right: 140,
        top: 60,
        bottom: 50,
        containLabel: false
      },
      xAxis: {
        type: 'category',
        name: 'Shift',
        nameLocation: 'middle',
        nameGap: 35,
        data: categories,
        axisLine: {
          lineStyle: {
            color: '#ccc'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
        interval: 0,
        color: '#666',
        fontSize: 11,
        formatter: (value: string) => {
          if (!value) {
            return '';
          }

          // example: "2026-07-22: 2ND SHIFT"
          const parts = value.split(':');

          if (parts.length >= 2) {
            const datePart = parts[0].trim();
            const remainingPart = parts.slice(1).join(':').trim();
            return `${datePart}\n${remainingPart}`;
          }

          // fallback: split by space if no colon
          const firstSpaceIndex = value.indexOf(' ');
          if (firstSpaceIndex > -1) {
            const firstPart = value.substring(0, firstSpaceIndex).trim();
            const secondPart = value.substring(firstSpaceIndex + 1).trim();
            return `${firstPart}\n${secondPart}`;
          }

          return value;
        }
      },
        nameTextStyle: {
          color: '#666',
          fontSize: 14
        }
      },
      yAxis: {
        type: 'value',
        name: 'Sample Rate (%)',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 35,
        min: 0,
        max: 3,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 12,
          formatter: (value: number) => `${value}%`
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e9edf5',
            type: 'solid'
          }
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 14
        }
      },
      series
    };
  }

  private getTooltipFormatter(params: any[]): string {
    if (!params || !params.length) {
      return '';
    }

    const label = params[0].axisValueLabel || params[0].axisValue;

    const validParams = params.filter((p: any) =>
      p.value !== null &&
      p.value !== undefined &&
      p.value !== ''
    );

    const rows = validParams.map((p: any) => {
      const color = p.color || '#999';
      const marker = `
        <span style="
          display:inline-block;
          width:10px;
          height:10px;
          margin-right:6px;
          background:${color};
          vertical-align:middle;
        "></span>
      `;

      return `
        <div style="display:flex; justify-content:space-between; gap:16px;">
          <span>${marker}${p.seriesName}</span>
          <span><b>${Number(p.value).toFixed(2)}%</b></span>
        </div>
      `;
    }).join('');

    return `
      <div style="min-width:190px;">
        <div style="margin-bottom:6px;">${label}</div>
        ${rows}
      </div>
    `;
  }
}